import { useState } from 'react';
import PropTypes from 'prop-types';
import { IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import useRoles from 'common/hooks/useRoles';

import { FILL_LATER_ACTIONABLE_STATUSES } from '../constants/fillLaterEligibility';
import CancelFillLaterDialog from './CancelFillLaterDialog';
import CreateFillLaterNowDialog from './CreateFillLaterNowDialog';
import MergeFillLaterDialog from './MergeFillLaterDialog';
import RescheduleFillLaterDialog from './RescheduleFillLaterDialog';
import UpdateFillLaterAddressDialog from './UpdateFillLaterAddressDialog';

/**
 * Row menu for the Fill Later queue. Disabled for CREATED / FAILED / MERGED rows and for a
 * user without the manage permission; the backend enforces both rules, this only keeps the menu
 * honest.
 *
 * Create now calls POM-6150's createFillLaterItemNow. Merge with parent order (POM-6182) is
 * offered only while the backend reports the row mergeable - the parent line item has not entered
 * shipment - and is otherwise shown disabled with the reason the backend gave.
 */
const FillLaterQueueOptions = ({ item }) => {
  const { manageFillLaterQueue } = useRoles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [createNowOpen, setCreateNowOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [addressOpen, setAddressOpen] = useState(false);
  const menuOpen = !!anchorEl;
  const isActionable = manageFillLaterQueue && FILL_LATER_ACTIONABLE_STATUSES.has(item.status);
  const disabledReason = !manageFillLaterQueue
    ? 'You do not have permission to act on Fill Later items'
    : 'No actions for a completed item';

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleRescheduleOpen = () => {
    handleMenuClose();
    setRescheduleOpen(true);
  };

  const handleCreateNowOpen = () => {
    handleMenuClose();
    setCreateNowOpen(true);
  };

  const handleMergeOpen = () => {
    handleMenuClose();
    setMergeOpen(true);
  };

  const handleCancelOpen = () => {
    handleMenuClose();
    setCancelOpen(true);
  };

  const handleAddressOpen = () => {
    handleMenuClose();
    setAddressOpen(true);
  };

  return (
    <>
      <Tooltip title={isActionable ? '' : disabledReason} arrow>
        {/* A disabled button emits no pointer events; the span carries the tooltip for it. */}
        <span data-testid={`WorkQueue-FillLaterQueue-${item.fillLaterItemId}-options`}>
          <IconButton
            id={`WorkQueue-FillLaterQueue-${item.fillLaterItemId}-options-button`}
            aria-label={`Fill later options ${item.referenceOrderId}`}
            aria-controls={
              menuOpen ? `WorkQueue-FillLaterQueue-${item.fillLaterItemId}-options-menu` : undefined
            }
            aria-haspopup='true'
            aria-expanded={menuOpen ? 'true' : undefined}
            disabled={!isActionable}
            onClick={handleMenuOpen}
          >
            <MoreVertIcon />
          </IconButton>
        </span>
      </Tooltip>

      <Menu
        id={`WorkQueue-FillLaterQueue-${item.fillLaterItemId}-options-menu`}
        aria-labelledby={`WorkQueue-FillLaterQueue-${item.fillLaterItemId}-options-button`}
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem onClick={handleCreateNowOpen}>Create now</MenuItem>
        <MenuItem onClick={handleRescheduleOpen}>Change the date</MenuItem>
        <MenuItem onClick={handleAddressOpen}>Change shipping address</MenuItem>
        {item.mergeable ? (
          <MenuItem onClick={handleMergeOpen}>Merge with parent order</MenuItem>
        ) : (
          <Tooltip title={item.mergeBlockedReason ?? 'This item can no longer be merged'} arrow>
            {/* A disabled MenuItem emits no pointer events; the span carries the tooltip for it. */}
            <span data-testid={`WorkQueue-FillLaterQueue-${item.fillLaterItemId}-merge-disabled`}>
              <MenuItem disabled>Merge with parent order</MenuItem>
            </span>
          </Tooltip>
        )}
        <MenuItem onClick={handleCancelOpen}>Cancel fill later</MenuItem>
      </Menu>

      {isActionable && (
        <>
          <CreateFillLaterNowDialog
            open={createNowOpen}
            onClose={() => setCreateNowOpen(false)}
            item={item}
          />
          <RescheduleFillLaterDialog
            open={rescheduleOpen}
            onClose={() => setRescheduleOpen(false)}
            item={item}
          />
          {item.mergeable && (
            <MergeFillLaterDialog
              open={mergeOpen}
              onClose={() => setMergeOpen(false)}
              item={item}
            />
          )}
          <UpdateFillLaterAddressDialog
            open={addressOpen}
            onClose={() => setAddressOpen(false)}
            item={item}
          />
          <CancelFillLaterDialog
            open={cancelOpen}
            onClose={() => setCancelOpen(false)}
            item={item}
          />
        </>
      )}
    </>
  );
};

FillLaterQueueOptions.propTypes = {
  item: PropTypes.shape({
    fillLaterItemId: PropTypes.string.isRequired,
    referenceOrderId: PropTypes.string,
    status: PropTypes.string.isRequired,
    mergeable: PropTypes.bool,
    mergeBlockedReason: PropTypes.string,
    fillLaterType: PropTypes.string,
    addressDirectoryId: PropTypes.string,
  }).isRequired,
};

export default FillLaterQueueOptions;
